import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { isToday, isYesterday } from 'date-fns';
import { BellOff, Loader2, WifiOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import type { AppNotification } from '@/types/Notification';
import type { RealtimeConnectionState } from '@/services/realtime';
import NotificationItem from './NotificationItem';
import { useDateFormat } from '@/hooks/useDateFormat';

interface NotificationListProps {
  notifications: AppNotification[];
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  loadingMore: boolean;
  hasMore: boolean;
  connection: RealtimeConnectionState;
  onSelect: (notification: AppNotification) => void;
  onLoadMore: () => void;
}

type Group = { key: string; label: string; items: AppNotification[] };

export const NotificationList: React.FC<NotificationListProps> = ({
  notifications,
  status,
  loadingMore,
  hasMore,
  connection,
  onSelect,
  onLoadMore,
}) => {
  const { t } = useTranslation();
  const df = useDateFormat();

  // Day headings. The list is already newest-first, so a single pass keeps the
  // groups in order without sorting again.
  const groups = useMemo<Group[]>(() => {
    const result: Group[] = [];

    notifications.forEach(notification => {
      const date = new Date(notification.created_at);
      const key = isToday(date)
        ? 'today'
        : isYesterday(date)
          ? 'yesterday'
          : date.toDateString();

      const label = isToday(date)
        ? t('notifications.groups.today.translation')
        : isYesterday(date)
          ? t('notifications.groups.yesterday.translation')
          : df.dayMonth(date);

      const last = result[result.length - 1];
      if (last?.key === key) {
        last.items.push(notification);
      } else {
        result.push({ key, label, items: [notification] });
      }
    });

    return result;
  }, [notifications, t]);

  if (status === 'loading' && notifications.length === 0) {
    return (
      <div className="flex flex-col gap-3 p-3">
        {[0, 1, 2].map(index => (
          <div key={index} className="flex gap-3">
            <Skeleton className="h-8 w-8 shrink-0 rounded-full" />
            <div className="flex flex-1 flex-col gap-2">
              <Skeleton className="h-3 w-2/3" />
              <Skeleton className="h-3 w-1/2" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (notifications.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 px-6 py-10 text-center">
        <BellOff className="h-8 w-8 text-muted-foreground/60" />
        <p className="text-sm font-medium text-foreground">
          {t('notifications.empty.title.translation')}
        </p>
        <p className="text-xs text-muted-foreground">
          {t('notifications.empty.description.translation')}
        </p>
      </div>
    );
  }

  return (
    // The cap has to reach the viewport, not just the Root. The Root is
    // overflow-hidden with an auto height, so h-full on the viewport resolves
    // to auto: it grows to fit every notification and the Root silently clips
    // the overflow — rendered, but unreachable, and no scrollbar because the
    // viewport never overflows. min() keeps it sane on short screens too.
    <ScrollArea className="max-h-[min(22rem,60vh)] [&>[data-radix-scroll-area-viewport]]:max-h-[min(22rem,60vh)]">
      <div className="flex flex-col p-1">
        {groups.map(group => (
          <div key={group.key} className="flex flex-col">
            <p className="px-3 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              {group.label}
            </p>
            {group.items.map(notification => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onSelect={onSelect}
              />
            ))}
          </div>
        ))}

        {hasMore && (
          <Button
            variant="ghost"
            size="sm"
            className="mx-2 my-2 text-xs text-muted-foreground"
            onClick={onLoadMore}
            disabled={loadingMore}
          >
            {loadingMore && <Loader2 className="mr-2 h-3 w-3 animate-spin" />}
            {t('notifications.load_more.translation')}
          </Button>
        )}

        {connection === 'disconnected' && (
          <p className="flex items-center gap-1.5 px-3 py-2 text-[11px] text-muted-foreground">
            <WifiOff className="h-3 w-3" />
            {t('notifications.reconnecting.translation')}
          </p>
        )}
      </div>
    </ScrollArea>
  );
};

export default NotificationList;
